import Controller from "./class.Controller.js";

/**
 * Stub class for Parsers.
 */
class Parser extends Controller {
	constructor(options) {
		super(options);
		this.cache = {};
	}

	/**
	 * Checks answers.
	 *
	 * @param {array} options The parseable options of the problem.
	 * @param {string} response the Student's submission.
	 * @returns Promise, resolves with index of a matching option. -1 if none.
	 */
	indexOfResponse(options, response) {
		return new Promise((resolve) => {
			resolve(-1);
		});
	}

	/**
	 * Clears the parser cache.
	 */
	resetCache() {
		this.cache = {};
	}
}

/**
 * A parser which works with all parser_types.
 * Hands off to appropriate parsers when necessary.
 *
 * Interestingly, this parser doesn't do any parsing itself.
 * It just outsources it to the others, which is why it is not
 * an option in z_data.
 *
 * This way, any Actions that need to parse but do not want to make
 * decisions about which parser to use, can simply pass their
 * options to this parser.
 */
class AutoParser extends Parser {
	constructor(options) {
		super(options);
	}

	/**
	 * Checks answers. It will try all offline methods of parsing first,
	 * and will only attempt backend parsing if necessary.
	 *
	 * @param {array} options The parseable options of the problem.
	 * @param {string} response the Student's submission.
	 * @returns Promise, resolves with index of a matching option. -1 if none.
	 */
	indexOfResponse(options, response) {
		return new Promise(async (resolve, reject) => {
			// Make sure all objects have a parser_type.
			var allOptions = options.map((n) => {
				return _.extend({parser_type: n.parser_type || "standard"}, n);
			});

			var index = -1;

			// Find the first occurrence of an option that needs the backend parser.
			var standardFirstIndex = allOptions
				.map((n) => n.parser_type)
				.indexOf("standard");

			var parsePromises = [];
			for (let key of Object.keys(Parsers)) {
				if (key !== "standard" && key !== "smart") {
					// Get the relevant options for each parser, storing
					// their original index on a copy.
					let parser = Parsers[key];
					let set = allOptions
						.map((val, idx) => {
							return _.extend({originalIndex: idx}, val);
						})
						.filter((val) => val.parser_type === key);
					if (set.length) {
						// Promise to parse this set, resolving the original index.
						parsePromises.push(
							new Promise(async (resolveParse, rejectParse) => {
								try {
									let parserIndex = await parser.indexOfResponse(set, response);
									resolveParse(
										parserIndex > -1 ? set[parserIndex].originalIndex : -1
									);
								} catch (e) {
									rejectParse(e);
								}
							})
						);
					}
				}
			}

			// Get the results of all offline parsing.
			// If one of the parses errored... oh well.
			var results = await Promise.all(parsePromises);
			var positives = results.filter((n) => {
				return isFinite(n) && n > -1;
			});
			// If we had a positive result, store the first one in the index variable.
			if (positives.length) {
				index = Math.min.apply(this, positives);
			}

			// If necessary, do a back end standard parse on just
			// the options that require it.
			if (index < 0 || index > standardFirstIndex) {
				let set = allOptions
					.map((val, idx) => {
						return _.extend({originalIndex: idx}, val);
					})
					.filter((val) => val.parser_type === "standard");
				if (set.length) {
					try {
						let standardIndex = await Parsers.standard.indexOfResponse(
							set,
							response
						);
						// Update the index variable if we've found a match.
						index =
							standardIndex > -1 ? set[standardIndex].originalIndex : index;
					} catch (e) {
						reject(e);
					}
				}
			}
			resolve(index);
		});
	}
}

/**
 * A parser which calls Transporter Ajax to find the answer.
 */
class AjaxParser extends Parser {
	constructor(options) {
		super(options);
		this.ajaxRunner = new AoPS.Ajax.ScriptRunner("/s/transporter/ajax.php", {
			filter_interval: 0,
		});
		if (!this.cache.errors) {
			this.cache.errors = {};
		}
		this.requestId = 0;
	}

	resetCache() {
		super.resetCache();
		this.cache.errors = {};
		this.requestId = 0;
	}

	/**
	 * Checks answers through the AJAX parser.
	 *
	 * @param {array} options The parseable options of the problem.
	 * @param {string} response The student's submission.
	 * @returns Promise, resolves with index of a matching option. -1 if none.
	 */
	async indexOfResponse(options, response) {
		const requestId = this.requestId++;
		const request = {response};

		// Build a list that the AJAX Parser will like.
		// We want it to return the index of any matching options.
		const check = [];
		for (let option of options) {
			let match = option.match;
			let type = option.answer_type || "standard";
			if (!_.isUndefined(match)) {
				check.push({match, type});
			}
		}

		// Hash the request so we can check the cache.
		const hash =
			response +
			"+" +
			JSON.stringify(_.map(check, (n) => JSON.stringify(n.match) + n.type));

		// If there are errors for this requested item we throw an error so they can retry
		// Note that we skip throwing if we are in replay mode so we get the full playback
		if (
			this.cache.errors &&
			!_.isUndefined(this.cache.errors[requestId]) &&
			!AoPS.Transporter.isReplay
		) {
			throw new Error("AjaxParser Dropped Call Error");
		} else if (!_.isUndefined(this.cache[hash])) {
			return this.cache[hash];
		} else {
			// Attach it to the request object.
			request.options = JSON.stringify(check);
			try {
				const result = await this.runUntil("check_answer_index", request);
				this.cache[hash] = result.response.index;
				return result.response.index;
			} catch (e) {
				if (!this.cache.errors) {
					this.cache.errors = {};
				}
				this.cache.errors[requestId] = 1;
				throw e;
			}
		}
	}

	async runUntil(alias, request, failLimit = 2) {
		let success = false;
		let i = 0;
		let error = null;
		let result = null;

		while (!success && i < failLimit) {
			try {
				// eslint-disable-next-line no-await-in-loop
				result = await this.run(alias, request);
				success = true;
			} catch (e) {
				i++;
				error = e;
			}
		}

		if (success) {
			return result;
		} else {
			throw error;
		}
	}

	run(alias, request) {
		return new Promise((resolve, reject) => {
			this.ajaxRunner.run(alias, request, (success, result) => {
				if (success) {
					resolve(result);
				} else {
					const error = new Error();
					error.name = result.error_code;
					error.message = result.error_msg;
					reject(error);
				}
			});
		});
	}
}

/**
 * A parser for string matching.
 */
class StringParser extends Parser {
	constructor(options) {
		super(options);
		this.verbatim = this.verbatim || false;
	}

	/**
	 * Checks answers through JavaScript string equality.
	 * If the parser is not set to verbatim mode, then it will
	 * trim whitespace and compare in lowercase for partial matches.
	 *
	 * @param {array} options The parseable options of the problem.
	 * @param {string} response The student's submission.
	 * @returns Promise, resolves with index of a matching option. -1 if none.
	 */
	indexOfResponse(options, response) {
		return new Promise((resolve, reject) => {
			try {
				var mappedOptions;
				if (!this.verbatim) {
					var cleanedResponse = response.trim().toLowerCase();
					mappedOptions = options.map((option) => {
						return option.match && option.match.length
							? option.match
									.map((n) => n.trim().toLowerCase())
									.filter((n) => cleanedResponse.indexOf(n) > -1).length > 0
							: true;
					});
				} else {
					mappedOptions = options.map((option) => {
						return option.match && option.match.length
							? option.match.indexOf(response) > -1
							: true;
					});
				}
				resolve(mappedOptions.indexOf(true));
			} catch (e) {
				reject(e);
			}
		});
	}
}

export const Parsers = {
	standard: new AjaxParser(),
	stringmatch: new StringParser(),
	verbatim: new StringParser({
		attributes: {
			verbatim: true,
		},
	}),
	js: new Parser(),
	auto: new AutoParser(),
};
